/**
 * Schichtplanung (/schichtplan):
 *  - Tab „Planraster": Wochen-Navigation (KW ±, Heute), Grid Tage (Mo–So) ×
 *    Schichten mit Mitarbeiter-Chips (abwesend ausgegraut + Grund-Badge,
 *    eingebucht mit Häkchen), Presence-Filter; Chip-Klick → RebookSheet.
 *  - Tab „Wochenplan": Matrix KW × Schicht = Team (Phase-1-Ergebnis).
 *  - Tab „Generator" (Gate personal_schreiben): Hauptweg „Schichtplan
 *    erstellen" (Ein-Klick U-32c) + „Planung zurücknehmen" (G-42) + Umplanen;
 *    die getrennten Phasen 1/2 bleiben als aufklappbarer „Erweitert"-Bereich.
 */

import { useMemo, useState } from "react"
import { keepPreviousData, useQuery } from "@tanstack/react-query"
import {
  CalendarPlus,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  PlayCircle,
  Repeat,
  Rows3,
  Undo2,
} from "lucide-react"
import { CreateShiftPlanSheet } from "@/components/planning/CreateShiftPlanSheet"
import { GeneratorDialog } from "@/components/planning/GeneratorDialog"
import { ReassignSheet } from "@/components/planning/ReassignSheet"
import { RebookSheet } from "@/components/planning/RebookSheet"
import { RetractShiftPlanDialog } from "@/components/planning/RetractShiftPlanDialog"
import { RolloutDialog } from "@/components/planning/RolloutDialog"
import { RotationStartCard } from "@/components/planning/RotationStartCard"
import { LoadingSpinner } from "@/components/shared"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { ABSENCE_REASONS } from "@/lib/absenceColors"
import { fmtDate } from "@/lib/format"
import {
  WEEKDAYS_DE,
  addDaysIso,
  isoWeekOf,
  mondayOfIsoWeek,
  todayIso,
  weeksInIsoYear,
} from "@/lib/isoWeek"
import { useCan } from "@/lib/permissions"
import { fetchAllShiftPlanEntries } from "@/lib/shiftPlanQuery"
import { cn } from "@/lib/utils"
import { api } from "@/services/api"
import type { PresenceFilter, ShiftPlanEntry } from "@/types"

// ---------------------------------------------------------------------------
// Tab 1: Planraster (Woche × Schicht)
// ---------------------------------------------------------------------------

const PRESENCE_OPTIONS: { value: PresenceFilter; label: string }[] = [
  { value: "all", label: "Alle" },
  { value: "present", label: "Anwesend" },
  { value: "absent", label: "Abwesend" },
  { value: "checkedIn", label: "Eingebucht" },
]

function EmployeeChip({
  entry,
  onClick,
  clickable,
}: {
  entry: ShiftPlanEntry
  onClick: () => void
  clickable: boolean
}) {
  const absent = entry.absenceReason !== null
  return (
    <button
      type="button"
      disabled={!clickable}
      onClick={onClick}
      title={`${entry.positionName}${entry.workplaceName ? ` · ${entry.workplaceName}` : ""}${
        entry.teamName ? ` · Team ${entry.teamName}` : ""
      }`}
      className={cn(
        "flex w-full items-center gap-1 rounded-md border px-1.5 py-0.5 text-left text-xs transition-colors",
        absent
          ? "border-dashed bg-muted/60 text-muted-foreground opacity-70"
          : "bg-card hover:bg-accent",
        !clickable && "cursor-default"
      )}
    >
      <span className="min-w-0 flex-1 truncate">{entry.employeeName}</span>
      {entry.isCheckedIn && <Check className="size-3 shrink-0 text-green-600" />}
      {absent && entry.absenceReason && (
        <Badge
          variant="outline"
          className={cn("px-1 py-0 text-[10px]", ABSENCE_REASONS[entry.absenceReason].badge)}
        >
          {ABSENCE_REASONS[entry.absenceReason].label}
        </Badge>
      )}
    </button>
  )
}

function PlanGrid() {
  const canWrite = useCan("personal_schreiben")
  const today = useMemo(todayIso, [])
  const [{ year, week }, setWeek] = useState(() => isoWeekOf(todayIso()))
  const [presence, setPresence] = useState<PresenceFilter>("all")
  const [rebookEntry, setRebookEntry] = useState<ShiftPlanEntry | null>(null)

  const monday = mondayOfIsoWeek(year, week)
  const days = Array.from({ length: 7 }, (_, i) => addDaysIso(monday, i))
  const from = days[0]
  const to = days[6]

  function shiftWeek(deltaDays: number) {
    setWeek(isoWeekOf(addDaysIso(monday, deltaDays)))
  }

  const shifts = useQuery({
    queryKey: ["shifts", { size: 100 }],
    queryFn: () => api.organization.shifts.list({ size: 100 }),
  })

  const entries = useQuery({
    queryKey: ["shiftPlan", { from, to, presence }],
    queryFn: () => fetchAllShiftPlanEntries({ from, to, presence }),
    placeholderData: keepPreviousData,
  })

  // Zeilen: Schichten-Stammdaten (rotation_order), nur solche mit Planzeilen
  // in der Woche ODER alle, falls noch gar nichts ausgerollt ist.
  const rows = useMemo(() => {
    const all = [...(shifts.data?.data ?? [])].sort(
      (a, b) =>
        Number(a.shiftModelId) - Number(b.shiftModelId) || a.rotationOrder - b.rotationOrder
    )
    const usedShiftIds = new Set((entries.data ?? []).map((e) => e.shiftId))
    const used = all.filter((s) => usedShiftIds.has(s.id))
    return used.length > 0 ? used : all
  }, [shifts.data, entries.data])

  const cellEntries = (shiftId: string, day: string) =>
    (entries.data ?? [])
      .filter((e) => e.shiftId === shiftId && e.date === day)
      .sort((a, b) => a.employeeName.localeCompare(b.employeeName, "de"))

  return (
    <div className="space-y-4">
      {/* Wochen-Navigation + Presence-Filter */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            aria-label="Vorherige Woche"
            onClick={() => shiftWeek(-7)}
          >
            <ChevronLeft />
          </Button>
          <span className="min-w-40 text-center text-sm font-medium tabular-nums">
            KW {week}/{year}
            <span className="block text-xs font-normal text-muted-foreground">
              {fmtDate(from)} – {fmtDate(to)}
            </span>
          </span>
          <Button
            variant="outline"
            size="icon"
            aria-label="Nächste Woche"
            onClick={() => shiftWeek(7)}
          >
            <ChevronRight />
          </Button>
          <Button variant="outline" onClick={() => setWeek(isoWeekOf(today))}>
            Heute
          </Button>
        </div>
        <div className="flex gap-1">
          {PRESENCE_OPTIONS.map((p) => (
            <button
              key={p.value}
              type="button"
              onClick={() => setPresence(p.value)}
              className={cn(
                "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                presence === p.value
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-input bg-transparent text-muted-foreground hover:bg-accent"
              )}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Grid Schichten × Tage */}
      <Card>
        <CardContent className="overflow-x-auto py-4">
          {entries.isLoading || shifts.isLoading ? (
            <LoadingSpinner fullPage />
          ) : (
            <div
              className="grid min-w-[900px] gap-px rounded-md bg-border"
              style={{ gridTemplateColumns: "8rem repeat(7, minmax(0, 1fr))" }}
            >
              <div className="bg-muted px-2 py-1.5 text-xs font-medium text-muted-foreground">
                Schicht
              </div>
              {days.map((d, i) => (
                <div
                  key={d}
                  className={cn(
                    "bg-muted px-2 py-1.5 text-center text-xs font-medium",
                    d === today ? "text-primary" : "text-muted-foreground"
                  )}
                >
                  {WEEKDAYS_DE[i]} {fmtDate(d).slice(0, 6)}
                </div>
              ))}
              {rows.map((shift) => (
                <div key={shift.id} className="contents">
                  <div className="flex items-center bg-card px-2 py-2 text-sm font-medium">
                    {shift.name}
                  </div>
                  {days.map((d) => {
                    const list = cellEntries(shift.id, d)
                    return (
                      <div key={d} className="min-h-14 space-y-1 bg-card p-1.5">
                        {list.map((e) => (
                          <EmployeeChip
                            key={e.id}
                            entry={e}
                            clickable={canWrite}
                            onClick={() => setRebookEntry(e)}
                          />
                        ))}
                      </div>
                    )
                  })}
                </div>
              ))}
              {rows.length === 0 && (
                <div className="col-span-8 bg-card p-6 text-sm text-muted-foreground">
                  Keine Schichten definiert.
                </div>
              )}
            </div>
          )}
          {!entries.isLoading && (entries.data ?? []).length === 0 && (
            <p className="pt-3 text-sm text-muted-foreground">
              Keine Planzeilen in KW {week}/{year}
              {presence !== "all" && " (mit aktivem Presence-Filter)"} — ggf. zuerst
              generieren und ausrollen (Tab „Generator").
            </p>
          )}
        </CardContent>
      </Card>

      <RebookSheet
        open={rebookEntry !== null}
        onOpenChange={(o) => !o && setRebookEntry(null)}
        fixedEntry={rebookEntry}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Tab 2: Wochenplan-Matrix (KW × Schicht = Team)
// ---------------------------------------------------------------------------

function WeekPlanMatrix() {
  const current = useMemo(() => isoWeekOf(todayIso()), [])
  const [year, setYear] = useState(String(current.year))
  const [fromWeek, setFromWeek] = useState("1")
  const [toWeek, setToWeek] = useState(String(weeksInIsoYear(current.year)))

  const params = {
    year: Number(year),
    fromWeek: fromWeek ? Number(fromWeek) : undefined,
    toWeek: toWeek ? Number(toWeek) : undefined,
    size: 500,
  }
  const query = useQuery({
    queryKey: ["weekPlans", params],
    queryFn: () => api.planning.weekPlans.list(params),
    enabled: Number(year) >= 2000,
    placeholderData: keepPreviousData,
  })

  const rows = useMemo(() => query.data?.data ?? [], [query.data])

  // Spalten: DISTINCT Schichten, Zeilen: DISTINCT KWs
  const shiftCols = useMemo(() => {
    const map = new Map<string, string>()
    for (const r of rows) map.set(r.shiftId, r.shiftName)
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1], "de"))
  }, [rows])

  const weeks = useMemo(
    () => [...new Set(rows.map((r) => r.week))].sort((a, b) => a - b),
    [rows]
  )

  const teamAt = (week: number, shiftId: string) =>
    rows.find((r) => r.week === week && r.shiftId === shiftId)?.teamName ?? "—"

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-28 space-y-1.5">
          <Label>Jahr</Label>
          <Input type="number" value={year} onChange={(e) => setYear(e.target.value)} />
        </div>
        <div className="w-28 space-y-1.5">
          <Label>KW von</Label>
          <Input
            type="number"
            min={1}
            max={53}
            value={fromWeek}
            onChange={(e) => setFromWeek(e.target.value)}
          />
        </div>
        <div className="w-28 space-y-1.5">
          <Label>KW bis</Label>
          <Input
            type="number"
            min={1}
            max={53}
            value={toWeek}
            onChange={(e) => setToWeek(e.target.value)}
          />
        </div>
        <span className="pb-2 text-sm text-muted-foreground">
          {query.data ? `${query.data.total} Wochenplan-Zeilen` : ""}
        </span>
      </div>

      <Card>
        <CardContent className="py-2">
          {query.isLoading ? (
            <LoadingSpinner fullPage />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-24">KW</TableHead>
                  {shiftCols.map(([id, name]) => (
                    <TableHead key={id}>{name}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {weeks.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={1 + Math.max(shiftCols.length, 1)}
                      className="text-muted-foreground"
                    >
                      Keine Wochenpläne im gewählten Bereich — zuerst generieren (Tab
                      „Generator").
                    </TableCell>
                  </TableRow>
                )}
                {weeks.map((w) => (
                  <TableRow key={w}>
                    <TableCell className="font-medium tabular-nums">
                      KW {w}/{year}
                    </TableCell>
                    {shiftCols.map(([id]) => (
                      <TableCell key={id}>{teamAt(w, id)}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Tab 3: Generator (Gate personal_schreiben)
// ---------------------------------------------------------------------------

function GeneratorTab({ onGoToGrid }: { onGoToGrid: () => void }) {
  const canWrite = useCan("personal_schreiben")
  const [createOpen, setCreateOpen] = useState(false)
  const [retractOpen, setRetractOpen] = useState(false)
  const [generatorOpen, setGeneratorOpen] = useState(false)
  const [rolloutOpen, setRolloutOpen] = useState(false)
  const [reassignOpen, setReassignOpen] = useState(false)
  const [advancedOpen, setAdvancedOpen] = useState(false)

  if (!canWrite) {
    return (
      <p className="text-sm text-muted-foreground">
        Keine Berechtigung für Generator/Rollout/Umplanen (personal_schreiben).
      </p>
    )
  }

  const advancedCards = [
    {
      icon: Rows3,
      title: "Phase 1: Wochenplan generieren",
      description:
        "Rotationsformel je (KW, Schicht) → Team über einen KW-Bereich; idempotent (G-17).",
      action: "Generieren …",
      onClick: () => setGeneratorOpen(true),
    },
    {
      icon: PlayCircle,
      title: "Phase 2: Schichtplan ausrollen",
      description:
        "Tages-Planzeilen aus den Wochenplänen: je Zeile × Team-Besetzung × alle 7 Kalendertage (G-18).",
      action: "Ausrollen …",
      onClick: () => setRolloutOpen(true),
    },
  ]

  return (
    <div className="space-y-4">
      {/* Hauptweg: Ein-Klick-Schichtplan (U-32c) */}
      <Card className="border-primary/40">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <CalendarPlus className="size-4 text-primary" /> Schichtplan erstellen
          </CardTitle>
          <CardDescription>
            Ein Schritt: Vorschau prüfen → Wochenplan + Tagesplan werden zusammen erstellt.
            Beim ersten Start eines Schichtmodells wird die Startbelegung erfragt.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => setCreateOpen(true)}>Schichtplan erstellen …</Button>
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        {/* Kontrollierte Rücknahme (G-42) */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Undo2 className="size-4 text-muted-foreground" /> Planung zurücknehmen
            </CardTitle>
            <CardDescription>
              Löscht Tagesplan + Wochenplan eines Schichtmodells im KW-Bereich. Tage mit
              bereits gebuchter Anwesenheit bleiben geschützt stehen (G-42).
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" onClick={() => setRetractOpen(true)}>
              Zurücknehmen …
            </Button>
          </CardContent>
        </Card>

        {/* Umplanen / Ersatz (G-22) */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Repeat className="size-4 text-muted-foreground" /> Umplanen / Ersatz
            </CardTitle>
            <CardDescription>
              Planzeilen eines Mitarbeiters im Zeitraum auf eine Ersatz-Stellenzuordnung
              und/oder Schicht umplanen; Abwesende → Konfliktliste (G-22).
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" onClick={() => setReassignOpen(true)}>
              Umplanen …
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Erweitert: die getrennten Phasen 1/2 + Rotationsstart, minimal-invasiv */}
      <div className="space-y-4">
        <button
          type="button"
          onClick={() => setAdvancedOpen((v) => !v)}
          className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
          aria-expanded={advancedOpen}
        >
          <ChevronDown
            className={cn("size-4 transition-transform", !advancedOpen && "-rotate-90")}
          />
          Erweitert: getrennte Schritte (Phase 1 / Phase 2) & Rotationsstart
        </button>
        {advancedOpen && (
          <div className="grid gap-4 md:grid-cols-3">
            <RotationStartCard />
            {advancedCards.map((c) => (
              <Card key={c.title}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <c.icon className="size-4 text-muted-foreground" /> {c.title}
                  </CardTitle>
                  <CardDescription>{c.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button variant="outline" onClick={c.onClick}>
                    {c.action}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <CreateShiftPlanSheet
        open={createOpen}
        onOpenChange={setCreateOpen}
        onGoToGrid={onGoToGrid}
      />
      <RetractShiftPlanDialog open={retractOpen} onOpenChange={setRetractOpen} />
      <GeneratorDialog open={generatorOpen} onOpenChange={setGeneratorOpen} />
      <RolloutDialog open={rolloutOpen} onOpenChange={setRolloutOpen} />
      <ReassignSheet open={reassignOpen} onOpenChange={setReassignOpen} />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Seite
// ---------------------------------------------------------------------------

export function ShiftPlanPage() {
  const [tab, setTab] = useState("grid")

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold tracking-tight">Schichtplan</h1>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="grid">Planraster</TabsTrigger>
          <TabsTrigger value="matrix">Wochenplan</TabsTrigger>
          <TabsTrigger value="generator">Generator</TabsTrigger>
        </TabsList>
        <TabsContent value="grid">
          <PlanGrid />
        </TabsContent>
        <TabsContent value="matrix">
          <WeekPlanMatrix />
        </TabsContent>
        <TabsContent value="generator">
          <GeneratorTab onGoToGrid={() => setTab("grid")} />
        </TabsContent>
      </Tabs>
    </div>
  )
}
